/**
 * Bitrix24 JavaScript SDK integration.
 * This module handles communication with the Bitrix24 portal when running inside an iframe.
 */

declare global {
  interface Window {
    BX24?: Bitrix24SDK;
  }
}

interface Bitrix24SDK {
  init(callback: () => void): void;
  install(callback?: () => void): void;
  installFinish(): void;
  getAuth(): Bitrix24Auth | null;
  refreshAuth(callback: (auth: Bitrix24Auth) => void): void;
  callMethod(
    method: string,
    params?: Record<string, any>,
    callback?: (result: any) => void
  ): void;
  callBatch(
    calls: Record<string, [string, Record<string, any>?]>,
    callback?: (result: any) => void
  ): void;
  placement: {
    info(): PlacementInfo;
    getInterface(callback: (data: any) => void): void;
    call(command: string, params?: Record<string, any>, callback?: (data: any) => void): void;
  };
  fitWindow(): void;
  resizeWindow(width: number, height: number, callback?: () => void): void;
  scrollParentWindow(scroll: number, callback?: () => void): void;
  isAdmin(): boolean;
  getLang(): string;
  getDomain(isOrigin?: boolean): string;
}

interface Bitrix24Auth {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  domain: string;
  member_id: string;
  user_id: string;
}

interface PlacementInfo {
  placement: string;
  options: Record<string, any>;
}

class Bitrix24Service {
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  /**
   * Checks if we're running inside a Bitrix24 iframe.
   */
  isInBitrix24(): boolean {
    return typeof window !== 'undefined' && !!window.BX24;
  }

  /**
   * Initializes the Bitrix24 SDK.
   * Returns true if running inside Bitrix24, false otherwise.
   */
  async init(): Promise<boolean> {
    if (this.initialized) return this.isInBitrix24();
    if (this.initPromise) {
      await this.initPromise;
      return this.isInBitrix24();
    }

    this.initPromise = new Promise((resolve, reject) => {
      if (!this.isInBitrix24()) {
        console.log('Not running inside Bitrix24 iframe');
        resolve();
        return;
      }

      try {
        window.BX24!.init(() => {
          this.initialized = true;
          console.log('Bitrix24 SDK initialized');
          resolve();
        });
      } catch (error) {
        reject(error);
      }
    });

    await this.initPromise;
    return this.isInBitrix24();
  }

  /**
   * Gets the current authentication data.
   */
  getAuth(): Bitrix24Auth | null {
    if (!this.isInBitrix24()) return null;
    return window.BX24!.getAuth();
  }

  /**
   * Refreshes the authentication token.
   */
  async refreshAuth(): Promise<Bitrix24Auth> {
    return new Promise((resolve, reject) => {
      if (!this.isInBitrix24()) {
        reject(new Error('Not in Bitrix24'));
        return;
      }

      window.BX24!.refreshAuth((auth) => {
        resolve(auth);
      });
    });
  }

  /**
   * Gets the portal domain.
   */
  getDomain(): string {
    if (!this.isInBitrix24()) return '';
    return window.BX24!.getDomain();
  }

  /**
   * Gets the current language.
   */
  getLang(): string {
    if (!this.isInBitrix24()) return 'pt';
    return window.BX24!.getLang();
  }

  /**
   * Checks if the current user is an admin.
   */
  isAdmin(): boolean {
    if (!this.isInBitrix24()) return true;
    return window.BX24!.isAdmin();
  }

  /**
   * Gets placement info.
   */
  getPlacementInfo(): PlacementInfo | null {
    if (!this.isInBitrix24()) return null;
    return window.BX24!.placement.info();
  }

  /**
   * Calls a Bitrix24 REST API method.
   */
  async callMethod<T = any>(method: string, params?: Record<string, any>): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.isInBitrix24()) {
        reject(new Error('Not in Bitrix24'));
        return;
      }

      window.BX24!.callMethod(method, params, (result) => {
        if (result.error()) {
          reject(new Error(result.error()));
        } else {
          resolve(result.data());
        }
      });
    });
  }

  /**
   * Resizes the iframe window.
   */
  resizeWindow(width: number, height: number): void {
    if (!this.isInBitrix24()) return;
    window.BX24!.resizeWindow(width, height);
  }

  /**
   * Fits the window to content.
   */
  fitWindow(): void {
    if (!this.isInBitrix24()) return;
    window.BX24!.fitWindow();
  }

  /**
   * Completes the installation process.
   */
  installFinish(): void {
    if (!this.isInBitrix24()) return;
    window.BX24!.installFinish();
  }

  /**
   * Gets auth data (alias for getAuth for compatibility).
   */
  async getAuthData(): Promise<Bitrix24Auth | null> {
    return this.getAuth();
  }

  /**
   * Resizes the frame to fit content (alias for fitWindow).
   */
  resizeFrame(): void {
    this.fitWindow();
  }
}

export const bitrix24 = new Bitrix24Service();
export type { Bitrix24Auth, PlacementInfo };
